/* eslint-disable @typescript-eslint/no-explicit-any */
// services/mpesaService.ts

export interface MpesaPaymentRequest {
  amount: number;
  phoneNumber: string;
  packageId?: string;
  plan?: string;
  currency?: string;
  isAnnual?: boolean;
}

export interface MpesaPaymentResponse {
  success: boolean;
  message: string;
  code: string;
  data?: {
    paymentId: string;
    checkoutRequestID: string;
    customerMessage: string;
    amount: number;
    currency: string;
    fees: number;
    netAmount: number;
  };
  payment?: any;
  error?: string;
}

export interface MpesaQueryResponse {
  success: boolean;
  payment?: any;
  queryResult?: any;
  code: string;
  error?: string;
}

class MpesaService {
  private baseURL: string;

  constructor() {
    // Use the same environment variable as your event service
    this.baseURL = import.meta.env.VITE_API_BASE;
  }

  // Enhanced token retrieval to match your event service
  private getAuthToken(): string | null {
    if (typeof window !== "undefined") {
      const token =
        localStorage.getItem("token") ||
        sessionStorage.getItem("token") ||
        localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken") ||
        null;

      console.log("🔐 MpesaService Token lookup:", {
        foundToken: !!token,
        localStorageToken: !!localStorage.getItem("token"),
        sessionStorageToken: !!sessionStorage.getItem("token"),
      });

      return token;
    }
    return null;
  }

  // Enhanced auth headers with debugging
  private getAuthHeaders(): HeadersInit {
    const token = this.getAuthToken();
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
      console.log(
        "✅ MpesaService Authorization header added with token:",
        `${token.substring(0, 20)}...`,
      );
    } else {
      console.warn("❌ MpesaService: No token found for authenticated request");
    }

    return headers;
  }

  // Enhanced response handler with better error handling
  private async handleResponse(response: Response): Promise<any> {
    console.log("📨 MpesaService Response status:", response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error("❌ MpesaService Request failed:", {
        status: response.status,
        statusText: response.statusText,
        error: errorText,
      });

      let errorMessage = `Request failed with status ${response.status}`;
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.error || errorData.message || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }

      throw new Error(errorMessage);
    }

    const responseText = await response.text();
    if (!responseText) {
      throw new Error("Empty response from server");
    }

    try {
      return JSON.parse(responseText);
    } catch (error) {
      console.error("❌ MpesaService JSON parse error:", error);
      throw new Error("Invalid JSON response from server");
    }
  }

  async initiatePayment(
    request: MpesaPaymentRequest,
  ): Promise<MpesaPaymentResponse> {
    const maxRetries = 3;
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(
          `🌐 MpesaService initiating payment (attempt ${attempt}/${maxRetries}):`,
          `${this.baseURL}/payments/methods/mpesa/initiate`,
        );

        const response = await fetch(
          `${this.baseURL}/payments/methods/mpesa/initiate`,
          {
            method: "POST",
            headers: this.getAuthHeaders(),
            body: JSON.stringify({
              amount: request.amount,
              phoneNumber: request.phoneNumber,
              packageId: request.packageId,
              plan: request.plan,
              currency: request.currency || "KES",
              isAnnual: request.isAnnual || false,
            }),
          },
        );

        // Handle rate limiting specifically
        if (response.status === 429) {
          const retryAfter = response.headers.get("Retry-After");
          const waitTime = retryAfter ? parseInt(retryAfter) * 1000 : 60000; // Default 60 seconds

          console.log(
            `⏰ Rate limited. Waiting ${waitTime / 1000} seconds before retry...`,
          );

          if (attempt < maxRetries) {
            await new Promise((resolve) => setTimeout(resolve, waitTime));
            continue; // Retry the request
          }
        }

        return await this.handleResponse(response);
      } catch (error) {
        lastError = error as Error;
        console.error(
          `M-Pesa payment initiation error (attempt ${attempt}):`,
          error,
        );

        // If it's not the last attempt, wait and retry
        if (attempt < maxRetries) {
          const waitTime = Math.pow(2, attempt) * 1000; // Exponential backoff
          console.log(`⏰ Waiting ${waitTime / 1000} seconds before retry...`);
          await new Promise((resolve) => setTimeout(resolve, waitTime));
        }
      }
    }

    // If we get here, all retries failed
    throw (
      lastError || new Error("M-Pesa payment failed after all retry attempts")
    );
  }

  async queryPayment(
    paymentId: string,
    checkoutRequestID?: string,
  ): Promise<MpesaQueryResponse> {
    try {
      console.log("🌐 MpesaService querying payment:", {
        paymentId,
        checkoutRequestID,
      });

      const response = await fetch(
        `${this.baseURL}/payments/methods/mpesa/query`,
        {
          method: "POST",
          headers: this.getAuthHeaders(),
          body: JSON.stringify({
            paymentId,
            checkoutRequestID,
          }),
        },
      );

      return await this.handleResponse(response);
    } catch (error) {
      console.error("M-Pesa payment query error:", error);
      throw error;
    }
  }

  validatePhoneNumber(phoneNumber: string): boolean {
    // Remove any non-digit characters
    const cleaned = phoneNumber.replace(/\D/g, "");

    // More flexible validation for Kenyan numbers
    return (
      (cleaned.length === 12 && cleaned.startsWith("254")) || // 2547XXXXXXXX
      (cleaned.length === 10 && cleaned.startsWith("07")) || // 07XXXXXXXX
      (cleaned.length === 9 && cleaned.startsWith("7")) || // 7XXXXXXXX
      (cleaned.length === 13 && cleaned.startsWith("254")) || // 2547XXXXXXXXX (with country code)
      (cleaned.length === 12 && cleaned.startsWith("254")) // Alternative format
    );
  }

  formatPhoneNumber(phoneNumber: string): string {
    const cleaned = phoneNumber.replace(/\D/g, "");

    if (
      cleaned.startsWith("254") &&
      (cleaned.length === 12 || cleaned.length === 13)
    ) {
      return cleaned.substring(0, 12); // Ensure 12 digits max for M-Pesa
    } else if (cleaned.startsWith("07") && cleaned.length === 10) {
      return "254" + cleaned.substring(1);
    } else if (cleaned.startsWith("7") && cleaned.length === 9) {
      return "254" + cleaned;
    } else if (cleaned.startsWith("254") && cleaned.length === 12) {
      return cleaned;
    }

    throw new Error(
      "Invalid phone number format. Use 07XXXXXXXX or 2547XXXXXXXX",
    );
  }

  // ✅ ENHANCED POLLING STRATEGY WITH EXPONENTIAL BACKOFF
  async pollPaymentStatus(
  paymentId: string,
  checkoutRequestID: string,
  maxAttempts: number = 10,
  initialDelay: number = 30000 // ✅ 30 seconds
): Promise<MpesaQueryResponse> {
  let attempts = 0;

  const delays = [
    5000, 10000, 15000, 20000, 30000, 30000, 30000, 30000, 30000, 30000,
    30000, 30000,
  ];

  // ✅ CRITICAL FIX: WAIT BEFORE FIRST QUERY
  console.log(`⏳ Waiting ${initialDelay / 1000}s before first query...`);
  await new Promise((resolve) => setTimeout(resolve, initialDelay));

  while (attempts < maxAttempts) {
    try {
      attempts++;
      console.log(`🔍 Polling attempt ${attempts}/${maxAttempts}`);

      const result = await this.queryPayment(paymentId, checkoutRequestID);

      if (result.payment?.status === "successful") return result;
      if (["failed", "cancelled"].includes(result.payment?.status)) return result;

      if (attempts < maxAttempts) {
        const delay = delays[attempts - 1] || 30000;
        console.log(`⏰ Next poll in ${delay / 1000}s`);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    } catch (error) {
      console.error(`❌ Polling error (attempt ${attempts}):`, error);

      if (attempts < maxAttempts) {
        const delay = delays[attempts - 1] || 30000;
        await new Promise((resolve) => setTimeout(resolve, delay));
      } else {
        throw error;
      }
    }
  }

  return this.queryPayment(paymentId, checkoutRequestID);
}

  // Alternative polling strategy with configurable delays
  async pollPaymentStatusWithStrategy(
    paymentId: string,
    checkoutRequestID: string,
    strategy: {
      maxAttempts?: number;
      delays?: number[]; // Custom delays array
      onProgress?: (attempt: number, status: string) => void;
    } = {},
  ): Promise<MpesaQueryResponse> {
    const maxAttempts = strategy.maxAttempts || 10;
    const delays = strategy.delays || [
      5000, 10000, 15000, 20000, 30000, 30000, 30000, 30000, 30000, 30000,
      30000, 30000,
    ];

    let attempts = 0;

    while (attempts < maxAttempts) {
      attempts++;

      try {
        if (strategy.onProgress) {
          strategy.onProgress(attempts, "polling");
        }

        console.log(`🔍 Smart polling attempt ${attempts}/${maxAttempts}`);
        const result = await this.queryPayment(paymentId, checkoutRequestID);

        const status = result.payment?.status;

        // Final statuses that break the polling loop
        if (["successful", "failed", "cancelled"].includes(status)) {
          console.log(`✅ Polling completed with final status: ${status}`);
          if (strategy.onProgress) {
            strategy.onProgress(attempts, status);
          }
          return result;
        }

        // If not final status, wait for next attempt
        if (attempts < maxAttempts) {
          const delay = delays[attempts - 1] || delays[delays.length - 1];
          console.log(`⏰ Next poll in ${delay / 1000}s (status: ${status})`);
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      } catch (error) {
        console.error(`❌ Smart polling error (attempt ${attempts}):`, error);

        if (attempts < maxAttempts) {
          const delay = delays[attempts - 1] || delays[delays.length - 1];
          console.log(`⏰ Retrying after error in ${delay / 1000}s`);
          await new Promise((resolve) => setTimeout(resolve, delay));
        } else {
          throw error;
        }
      }
    }

    // Final attempt to get status
    const finalResult = await this.queryPayment(paymentId, checkoutRequestID);
    console.log("🏁 Smart polling completed with timeout");
    return finalResult;
  }

  // Additional helper method to check payment status without polling
  async getPaymentStatus(paymentId: string): Promise<MpesaQueryResponse> {
    return this.queryPayment(paymentId);
  }
}

export const mpesaService = new MpesaService();

// ✅ DEDICATED POLLING SERVICE CLASS
export class MpesaPollingService {
  private attempts: number;
  private maxAttempts: number;
  private delays: number[];

  constructor(maxAttempts: number = 10) {
    this.attempts = 0;
    this.maxAttempts = maxAttempts;
    // Exponential backoff: 5s, 10s, 15s, 20s, then 30s intervals
    this.delays = [
      5000, 10000, 15000, 20000, 30000, 30000, 30000, 30000, 30000, 30000,
      30000, 30000,
    ];
  }

  async pollPaymentStatus(
    paymentId: string,
    checkoutRequestID?: string,
  ): Promise<MpesaQueryResponse> {
    if (this.attempts >= this.maxAttempts) {
      throw new Error("Payment timeout - please try again");
    }

    const delay = this.delays[this.attempts] || 30000;

    if (this.attempts > 0) {
      console.log(
        `⏰ Polling delay: ${delay / 1000}s (attempt ${this.attempts + 1}/${this.maxAttempts})`,
      );
      await new Promise((resolve) => setTimeout(resolve, delay));
    }

    this.attempts++;

    try {
      const result = await mpesaService.queryPayment(
        paymentId,
        checkoutRequestID,
      );

      // Check if we have a final status
      if (
        ["successful", "failed", "cancelled"].includes(result.payment?.status)
      ) {
        console.log(
          `✅ Polling completed with final status: ${result.payment.status}`,
        );
        return result;
      }

      // If not final status and we have more attempts, continue polling
      if (this.attempts < this.maxAttempts) {
        return this.pollPaymentStatus(paymentId, checkoutRequestID);
      } else {
        console.log(
          "⏰ Max polling attempts reached, returning current status",
        );
        return result;
      }
    } catch (error) {
      console.error(`❌ Polling attempt ${this.attempts} failed:`, error);

      // If we have more attempts, continue polling even after errors
      if (this.attempts < this.maxAttempts) {
        return this.pollPaymentStatus(paymentId, checkoutRequestID);
      } else {
        throw new Error(
          `Payment polling failed after ${this.maxAttempts} attempts`,
        );
      }
    }
  }

  // Reset the polling service for reuse
  reset(): void {
    this.attempts = 0;
  }

  // Get current polling progress
  getProgress(): { attempts: number; maxAttempts: number; progress: number } {
    const progress = (this.attempts / this.maxAttempts) * 100;
    return {
      attempts: this.attempts,
      maxAttempts: this.maxAttempts,
      progress: Math.min(progress, 100),
    };
  }
}

export default mpesaService;
